// CLI v0.8 — `cloudgrid env set [<name>] KEY=VAL ...`. Spec v17 §13.8.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';

interface PatchResponse {
  status: string;
  keys_set: string[];
  keys_unset: string[];
  restarting: boolean;
}

export interface RunEnvSetOpts {
  client: IV2Client;
  name: string | undefined;
  pairs: string[];
  cwd?: string;
}

export async function runEnvSet(opts: RunEnvSetOpts): Promise<void> {
  if (opts.pairs.length === 0) {
    throw new V2ApiError(400, 'VALIDATION_FAILED', 'Expected at least one KEY=VALUE pair.');
  }
  const set = parsePairs(opts.pairs);
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);
  const resp = await opts.client.patch<PatchResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/env`,
    { set },
  );
  const n = resp.keys_set.length;
  process.stdout.write(
    `Set ${n} env var${n === 1 ? '' : 's'}: ${resp.keys_set.join(', ')}\n`,
  );
  if (resp.restarting) process.stdout.write('Rolling restart dispatched.\n');
}

/**
 * `cloudgrid env set` accepts either positional pairs (`KEY=VAL`) or a
 * leading `[<name>]` followed by pairs. Slugs and entity_ids never contain
 * `=`, so a leading positional with `=` is unambiguously the first pair.
 */
export function splitNameAndPairs(args: string[]): { name: string | undefined; pairs: string[] } {
  if (args.length === 0) return { name: undefined, pairs: [] };
  if (args[0].includes('=')) return { name: undefined, pairs: args };
  return { name: args[0], pairs: args.slice(1) };
}

function parsePairs(pairs: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of pairs) {
    const eq = p.indexOf('=');
    if (eq < 1) {
      throw new V2ApiError(400, 'VALIDATION_FAILED', `Expected KEY=VALUE, got '${p}'.`);
    }
    out[p.slice(0, eq)] = p.slice(eq + 1);
  }
  return out;
}

export const envSetCommand: CommanderCommand = new Command('set')
  .description(
    'Set env vars on a runtime (admin only). ' +
    'In-folder: cloudgrid env set KEY=VAL. ' +
    'Out-of-folder: cloudgrid env set <name> KEY=VAL.',
  )
  .argument('<args...>', 'KEY=VALUE pairs, optionally preceded by an entity name')
  .action(async (args: string[]) => {
    try {
      const { name, pairs } = splitNameAndPairs(args);
      const client = await getV2Client();
      await runEnvSet({ client, name, pairs });
    } catch (err) {
      await handleV2Error(err);
    }
  });
